import classNames from 'classnames';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import Typography from '@/components/atoms/typography/typography';
import CtaLinkList, {
  CtaLinkListProps,
} from '@/components/organisms/cta-link-list/cta-link-list';
import Column from '@/components/utilities/column/column';
import Container from '@/components/utilities/container/container';
import Row from '@/components/utilities/row/row';
import styles from './text-and-image-block.module.scss';

export interface TextAndImageBlockProps {
  /** Indicates where to position the text */
  alignment?: 'left' | 'right';
  /** The class name to apply */
  className?: string;
  /** An array of links to show */
  ctaLinks?: CtaLinkListProps['items'];
  /** The text to show as a headline */
  headline?: string;
  /** The image to show */
  image?: Content.Properties.ImageWithAspectRatio;
  /** The text to show as a caption */
  imageCaption?: string;
  /** The text to show as a pre headline */
  preHeadline?: string;
  /** The text to show as a sub headline */
  subHeadline?: string;
  /** The rich text to show as a description */
  text?: string;
}

const TextAndImageBlock = ({
  alignment = 'left',
  className,
  ctaLinks = [],
  headline,
  imageCaption,
  image,
  preHeadline,
  subHeadline,
  text,
}: TextAndImageBlockProps) => {
  return (
    <div
      className={classNames(styles.component, className, {
        [styles.isRightAligned]: alignment === 'right',
      })}
    >
      <Container>
        <Row className={styles.row}>
          {image && (
            <Column
              offset={{ laptop: alignment === 'left' ? 0 : 1 }}
              order={{ laptop: alignment === 'left' ? 2 : 1 }}
              width={{ laptop: 5 }}
            >
              <div className={styles.imageWrapper}>
                {image.url && (
                  <Image
                    alt={image.altText}
                    aspectRatio={image.aspectRatio}
                    className={styles.image}
                    src={image.url}
                    srcSet={{
                      desktop: {
                        width: 515,
                      },
                      laptop: {
                        width: 355,
                      },
                      tablet: {
                        width: 672,
                      },
                    }}
                    unoptimized
                    width={311}
                  />
                )}

                {imageCaption && (
                  <Typography
                    className={styles.imageCaption}
                    tagStyle="labelMedium"
                  >
                    {imageCaption}
                  </Typography>
                )}
              </div>
            </Column>
          )}

          <Column
            offset={{
              laptop: alignment === 'left' ? 2 : 0,
            }}
            order={{ laptop: 1 }}
            width={{ laptop: 4 }}
          >
            <div className={styles.contentWrapper}>
              {(headline || preHeadline) && (
                <div className={styles.headlineWrapper}>
                  {preHeadline && (
                    <Typography
                      className={styles.preHeadline}
                      tag="small"
                      tagStyle="labelMedium"
                    >
                      {preHeadline}
                    </Typography>
                  )}

                  {headline && (
                    <Typography
                      className={styles.headline}
                      tag="h2"
                      tagStyle="displayMedium"
                    >
                      {headline}
                    </Typography>
                  )}
                </div>
              )}

              {subHeadline && (
                <Typography
                  className={styles.subHeadline}
                  tag="h3"
                  tagStyle="bodyLargeBold"
                >
                  {subHeadline}
                </Typography>
              )}

              {text && <RichText text={text} />}

              {ctaLinks.length > 0 && <CtaLinkList items={ctaLinks} />}
            </div>
          </Column>
        </Row>
      </Container>
    </div>
  );
};

TextAndImageBlock.displayName = 'TextAndImageBlock';

export default TextAndImageBlock;
